import { IconButton } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Plus } from 'lucide-react';

import type { ClientDetailPass } from '@/types';
import { paths } from '@/navigation/paths';
import { TEST_IDS } from '@/testIds';
import { SectionRow } from '@/shared/ui';
import {
  bucketPasses,
  FuturePassesCard,
  PassCard,
  PassesEmptyState,
  PastPassesLink,
} from '@/features/passes';
import styles from './PassesBlock.module.css';

interface PassesBlockProps {
  clientId: string;
  passes: ClientDetailPass[];
  onPassClick: (pass: ClientDetailPass) => void;
  onAddPass: () => void;
}

export const PassesBlock: FC<PassesBlockProps> = ({ clientId, passes, onPassClick, onAddPass }) => {
  const { active, future, past } = useMemo(() => bucketPasses(passes), [passes]);
  const navigate = useNavigate();

  return (
    <div className={styles.block}>
      {passes.length > 0 && (
        <SectionRow
          className={styles.sectionHeader}
          label="Passes"
          labelTestId={TEST_IDS.passesSectionHeader}
          action={
            <IconButton
              size="md"
              variant="surface"
              onClick={onAddPass}
              aria-label="Add pass"
              data-testid={TEST_IDS.passesSectionAddButton}
            >
              <Plus size={24} aria-hidden="true" />
            </IconButton>
          }
        />
      )}

      {passes.length === 0 && (
        <div className={styles.cards}>
          <PassesEmptyState onAddPass={onAddPass} />
        </div>
      )}

      {active.length > 0 && (
        <div className={styles.cards} data-testid={TEST_IDS.activePassesGroup}>
          {active.map((pass) => (
            <PassCard key={pass.id} pass={pass} onClick={onPassClick} />
          ))}
        </div>
      )}

      {future.length > 0 && (
        <div className={styles.cards}>
          <FuturePassesCard passes={future} onPassClick={onPassClick} />
        </div>
      )}

      {past.length > 0 && (
        <PastPassesLink
          count={past.length}
          onClick={() => void navigate(paths.clientPastPasses(clientId))}
        />
      )}
    </div>
  );
};
